import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Form, Grid } from 'semantic-ui-react';
import { useForm, FieldValues, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Field } from '../../interfaces/Field';
import { EntityMetadata as Metadata } from '../../interfaces/EntityMetadata';
import { CustomModal, POSButton } from '@publicisgroupe/p-os-components';
import { getField } from '../../utils/getField';
import { Entity } from '../../interfaces/Entity';
import useAddEntityMutation from '../../hooks/mutation/useAddEntityMutation';
import { ModalTitle } from '../../enums/ModalTitle';
import useEditEntityMutation from '../../hooks/mutation/useEditEntityMutation';
import { FieldsType } from '../../constants/FieldsType';
import { useSchema } from '../../hooks/custom/useSchema';
import { ConditionalFieldsOperators } from '../../enums/ConditionalFieldsOperators';

import './EditableModal.less';
import { clsx } from 'clsx';

type Props = {
  modalTitle: string;
  entityData?: Entity;
  metadata: Metadata;
  open: boolean;
  onClose: () => void;
  onUpdate?: (documentId: string) => void;
  resetPagination: () => void;
  isAddMode: boolean;
};

export const EditableModal: FC<Props> = ({
  modalTitle,
  entityData,
  metadata,
  open,
  onClose,
  onUpdate,
  resetPagination,
  isAddMode,
}) => {
  const filteredFields: Field[] = useMemo(
    () => metadata.fields.filter((field: Field) => !field.ignore),
    [metadata]
  );
  const { schema } = useSchema(filteredFields);
  const {
    register,
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: !isAddMode ? { ...entityData } : {},
    mode: 'onSubmit',
  });

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const { mutateAsync: addMutate } = useAddEntityMutation();
  const { mutateAsync: editMutate } = useEditEntityMutation();

  const onSuccess = useCallback(() => {
    resetPagination();
    onUpdate?.(entityData?.id);
    handleClose();
  }, [entityData?.id, handleClose, onUpdate, resetPagination]);

  const sanitizeData = useCallback(
    (data: FieldValues) => {
      metadata?.fields.forEach((field: Field) => {
        const key = field.name;
        if (field.type === FieldsType.number) {
          data[key] = +data[key];
        }
        if (
          (field.type === FieldsType.multiSelect || field.isArray) &&
          !data[key]
        ) {
          data[key] = [];
        }
        if (field.type === FieldsType.bool && typeof data[key] !== 'boolean') {
          data[key] = field.required ? false : null;
        }
        if (
          field.conditionalField &&
          field.conditionalField.value !==
            data[field.conditionalField.fieldRef.toLowerCase()]
        ) {
          data[key] = '';
        }
      });
      return data;
    },
    [metadata?.fields]
  );

  const onSubmit = useCallback(
    async (data: FieldValues) => {
      const sanitizedData: FieldValues = sanitizeData(data);

      modalTitle === ModalTitle.add
        ? await addMutate({
            entityName: metadata.name,
            data: sanitizedData,
          }).then(() => onSuccess())
        : await editMutate({
            entityName: metadata.name,
            data: sanitizedData,
          }).then(() => onSuccess());
    },
    [sanitizeData, modalTitle, addMutate, metadata.name, editMutate, onSuccess]
  );

  const inputRefs = useMemo(
    () =>
      filteredFields.reduce((fieldsAccumulator, entity) => {
        fieldsAccumulator[entity.name] = React.createRef();
        return fieldsAccumulator;
      }, {}),
    [filteredFields]
  );

  const isConditionMet = useCallback(
    (field: Field) => {
      if (!field.conditionalField) return true;

      const { fieldRef, operator, value } = field.conditionalField;
      const fieldValue = watch(fieldRef.toLowerCase() as any);

      if (operator === ConditionalFieldsOperators.equals) {
        return fieldValue && fieldValue.toString() === value;
      }
      return false;
    },
    [watch]
  );

  useEffect(() => {
    for (const key in errors) {
      if (errors[key] && inputRefs[key].current) {
        inputRefs[key].current.scrollIntoView({ behavior: 'smooth' });
        break;
      }
    }
  }, [errors, inputRefs]);

  return (
    <CustomModal
      data-testid="editable-modal"
      className="config-view-modal"
      open={open}
      onClose={handleClose}
      centered
      size="large"
      header={`${modalTitle} ${metadata.name}`}
    >
      <CustomModal.Content>
        <Form>
          <Grid.Row columns={2}>
            {filteredFields?.map((field) => {
              return isConditionMet(field) ? (
                <Grid.Column
                  className={clsx('field-holder', {
                    fluid: field.type === FieldsType.xdmContext,
                  })}
                  ref={inputRefs[field.name]}
                  key={field.name}
                >
                  <Form.Field disabled={field.readOnly}>
                    <Controller
                      {...register(field.name as never)}
                      control={control}
                      render={({ field: formField, formState }) =>
                        getField(field, formField, formState)
                      }
                    />
                    {errors[field.name]?.message && (
                      <Form.Field
                        className="error"
                        label={errors[field.name]?.message}
                      />
                    )}
                  </Form.Field>
                </Grid.Column>
              ) : (
                <></>
              );
            })}
          </Grid.Row>
        </Form>
      </CustomModal.Content>
      <CustomModal.Actions>
        <POSButton onClick={handleClose}>Cancel</POSButton>
        <POSButton onClick={handleReset}>Reset</POSButton>
        <POSButton onClick={handleSubmit(onSubmit)}>Save</POSButton>
      </CustomModal.Actions>
    </CustomModal>
  );
};
